<template>
  <PaginaAdministrar
    titulo="Catálogo NCM / Siscomex"
    subtitulo="Gestão global da Nomenclatura Comum do Mercosul e atributos do CADA"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="podeVerAcoes"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-6">
          <label class="form-label">Buscar NCM ou Descrição</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Ex: 85171231 ou Smartphone..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Filtrar</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">{{ erro }}</div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="ncms"
      :loading="loading"
      empty-message="Nenhuma NCM encontrada no catálogo."
      row-key="idNcm"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarNcm($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idNcm }}</span>
      </template>
      <template #codigo="{ row }">
        <span class="fw-bold">{{ row.codigoNcm }}</span>
      </template>

      <template #descricao="{ row }">
        <div class="text-truncate" :title="row.descricao" style="max-width: 300px;">{{ row.descricao }}</div>
      </template>

      <template #unidade="{ row }">
        <span class="badge bg-secondary">{{ getTextoUnidade(row.lstUnidadeEstatistica) }}</span>
      </template>

      <template #validade="{ row }">
        <span class="small">
          {{ formatarDataBR(row.dataInicioValidade) }} <i class="bi bi-arrow-right mx-1 text-muted"></i> {{ formatarDataBR(row.dataFimValidade) || 'Vigente' }}
        </span>
      </template>

      <template #siscomex="{ row }">
        <i v-if="row.sincronizadoSiscomex" class="bi bi-check-circle-fill text-success" title="Sincronizado com Portal Único"></i>
        <i v-else class="bi bi-dash-circle text-warning" title="Pendente de Validação"></i>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1" v-if="podeVerAcoes">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" title="Editar NCM e Atributos" @click="editarNcm(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Ncm"
    :chave-primaria="auditoriaSelecionada.id"
    :tabelas-filhas="['AtributoNcm']"
    chave-estrangeira="IdNcm"
    :titulo-registro="auditoriaSelecionada.nome"
    @fechar="mostrarAuditoria = false"
  />
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onActivated } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { formatarDataBR } from '@/utils/useDataBR'
import { useMetadataListasStore } from '@/stores/metadataListas'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])
const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const metadataStore = useMetadataListasStore()

const listasLookup = reactive({})

const carregarListas = async () => {
  const result = await metadataStore.carregarListasParaTabelas(['Ncm'])
  Object.assign(listasLookup, result)
}

const getTextoUnidade = (id) => {
  if (!id) return '-'
  const item = (listasLookup.LstUnidadeEstatistica || []).find(u => String(u.idListaClienteItem) === String(id))
  return item ? item.textoLista : id
}

const ncms = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({ busca: '' })

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const podeVerAcoes = computed(() => authStore.isAdminInterno || authStore.pode('NCM_VISUALIZACAO') || authStore.pode('NCM_COMPLETO'))

const colunas = [
  { field: 'idNcm', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'codigoNcm', label: 'Código NCM', width: 130, sortable: true, slotName: 'codigo' },
  { field: 'descricao', label: 'Descrição Oficial', sortable: true, slotName: 'descricao' },
  { field: 'lstUnidadeEstatistica', label: 'Unidade', width: 100, align: 'center', sortable: true, slotName: 'unidade' },
  { field: 'dataInicioValidade', label: 'Validade', width: 180, align: 'center', sortable: true, slotName: 'validade' },
  { field: 'sincronizadoSiscomex', label: 'Siscomex', width: 140, align: 'center', sortable: true, slotName: 'siscomex' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (field === 'lstUnidadeEstatistica') {
    return [...data].sort((a, b) => {
      const cmp = (a.lstUnidadeEstatistica || '').localeCompare(b.lstUnidadeEstatistica || '', 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  return null
}

const handleSortChange = () => { buscarDados(1) }

const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (filtros.busca) params.append('termoBusca', filtros.busca)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/Ncms?${params.toString()}`)
    ncms.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Erro ao carregar NCMs.'
    ncms.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarNcm = async (ncm) => {
  // Busca o NCM completo com atributos via GetById, pois a listagem não inclui a coleção
  try {
    const response = await api.get(`/Ncms/${ncm.idNcm}`)
    emit('editar-item', response.data)
  } catch (error) {
    console.error('Erro ao carregar NCM completo para edição:', error)
    emit('show-notification', { message: 'Erro ao carregar dados completos do NCM.', type: 'error' })
  }
}

const abrirAuditoria = (ncm) => {
  auditoriaSelecionada.id = ncm.idNcm
  auditoriaSelecionada.nome = ncm.codigoNcm + ' - ' + ncm.descricao
  mostrarAuditoria.value = true
}

onMounted(() => {
  carregarListas()
  buscarDados(1)
})
onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

// GUARDIÃO: Re-busca os dados quando o AdminInterno troca de empresa
watch(() => authStore.empresaAtiva, () => {
  buscarDados(1)
})

defineExpose({ buscarDados })
</script>

<style scoped>
</style>
